import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Database, Sparkles, EyeOff, UserCheck, Building2, Mail, ArrowRight, Check,
} from 'lucide-react';

/* ----------------------------------------------------------------------------
 * Trust — public privacy & data page. Honest, plain English, no legalese
 * padding. Blue brand accent, editorial serif headings, hairline-bordered
 * cards. Matches ForCandidates.js / Pool.js / Pricing.js. No fabricated numbers.
 * -------------------------------------------------------------------------- */

const BLUE = '#2563EB';

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

const COLLECT = [
  'What you tell us: the roles, seniority, and locations you want, plus your skills and the contact details you choose to share.',
  'How you use the product: which roles you view or mark interest in, and whether you’ve marked yourself open — this is what powers matching and intent.',
  'Account basics: your email and login, so you can sign in and manage your profile.',
];

const PROMISES = [
  'We never spam you.',
  'We never sell your data.',
  'Your contact details stay masked until you choose to share them.',
  'You can remove yourself anytime.',
];

function Trust() {
  return (
    <div className="bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-white to-[#F8FAFC] dark:from-gray-950 dark:to-gray-950">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center lg:px-8 lg:py-24">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
            <ShieldCheck size={15} style={{ color: BLUE }} />
            Trust &amp; privacy
          </motion.div>
          <motion.h1 {...reveal} transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-6 font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white sm:text-5xl">
            Your data, in plain English.
          </motion.h1>
          <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.1 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            No legalese padding. Here’s exactly what we collect, how matching and intent work, and the control you keep over your own profile.
          </motion.p>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-6 py-20 lg:px-8 space-y-8">
        {/* ===== WHAT WE COLLECT ===== */}
        <motion.section {...reveal} transition={{ duration: 0.5 }}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: BLUE }}>
            <Database size={20} strokeWidth={1.8} />
          </div>
          <h2 className="mt-5 font-serif text-2xl font-semibold text-gray-900 dark:text-white">What we collect</h2>
          <p className="mt-3 leading-relaxed text-gray-600 dark:text-gray-300">
            Only what we need to match you to roles and let recruiters find motivated candidates. Nothing more.
          </p>
          <ul className="mt-5 space-y-3">
            {COLLECT.map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-[15px] text-gray-700 dark:text-gray-200">
                <Check size={18} className="mt-0.5 flex-none" style={{ color: BLUE }} /> {t}
              </li>
            ))}
          </ul>
        </motion.section>

        {/* ===== HOW MATCHING + INTENT WORK ===== */}
        <motion.section {...reveal} transition={{ duration: 0.5 }}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: BLUE }}>
            <Sparkles size={20} strokeWidth={1.8} />
          </div>
          <h2 className="mt-5 font-serif text-2xl font-semibold text-gray-900 dark:text-white">How matching &amp; intent scoring work</h2>
          <p className="mt-3 leading-relaxed text-gray-600 dark:text-gray-300">
            <span className="font-semibold text-gray-900 dark:text-white">Matching</span> compares the targets you set — roles,
            seniority, location — and your skills against a recruiter’s open role. We rank by how well those line up. We don’t
            scrape stale résumés or guess about you from the open web; matching uses what you chose to tell us.
          </p>
          <p className="mt-4 leading-relaxed text-gray-600 dark:text-gray-300">
            <span className="font-semibold text-gray-900 dark:text-white">Intent</span> reflects how actively you’re looking right
            now — whether you’ve marked yourself open, how recently you’ve been active, and whether you’re engaging with roles like
            this one. It helps recruiters see who’s open today rather than who merely exists. Intent is never a judgment of your
            ability or worth, and a low score never blocks you from anything.
          </p>
        </motion.section>

        {/* ===== NEVER SPAM / NEVER SELL ===== */}
        <motion.section {...reveal} transition={{ duration: 0.5 }}
          className="rounded-2xl border-2 bg-white dark:bg-gray-900 p-8" style={{ borderColor: '#BFDBFE' }}>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>
            <ShieldCheck size={15} /> Our promise
          </div>
          <h2 className="mt-4 font-serif text-2xl font-semibold text-gray-900 dark:text-white">
            We never spam you and never sell your data.
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {PROMISES.map((t) => (
              <div key={t} className="flex items-start gap-2.5 text-[15px] font-medium text-gray-700 dark:text-gray-200">
                <Check size={18} className="mt-0.5 flex-none" style={{ color: BLUE }} /> {t}
              </div>
            ))}
          </div>
        </motion.section>

        {/* ===== CANDIDATE CONTROL ===== */}
        <motion.section {...reveal} transition={{ duration: 0.5 }}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: BLUE }}>
            <EyeOff size={20} strokeWidth={1.8} />
          </div>
          <h2 className="mt-5 font-serif text-2xl font-semibold text-gray-900 dark:text-white">You control your visibility</h2>
          <p className="mt-3 leading-relaxed text-gray-600 dark:text-gray-300">
            Recruiters only ever see what you’ve agreed to share. Your name and contact details stay masked in search results until
            a recruiter reveals them — and you decide whether to be in the pool at all. You can pause your visibility, edit what you
            share, or remove yourself entirely whenever you want, all from a self-serve manage link in your{' '}
            <Link to="/account" className="font-semibold" style={{ color: BLUE }}>account settings</Link>. Removal takes effect right away.
          </p>
        </motion.section>

        {/* ===== RECRUITER DATA / CONSENT ===== */}
        <motion.section {...reveal} transition={{ duration: 0.5 }}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: BLUE }}>
            <UserCheck size={20} strokeWidth={1.8} />
          </div>
          <h2 className="mt-5 font-serif text-2xl font-semibold text-gray-900 dark:text-white">For recruiters: the pool is real and consented</h2>
          <p className="mt-3 leading-relaxed text-gray-600 dark:text-gray-300">
            Every candidate in the pool joined on their own and chose to be searchable — there are no scraped profiles and no
            bought lists. That’s the point: you’re reaching people who actually raised their hand, which is why outreach here is warm
            rather than cold. We expect recruiters to treat that consent with respect and to reach out only about genuine roles.
          </p>
          <div className="mt-5">
            <Building2 size={18} className="inline-block mr-2 align-text-bottom" style={{ color: BLUE }} />
            <span className="text-sm text-gray-500 dark:text-gray-400">Pool integrity is a feature, not a footnote.</span>
          </div>
        </motion.section>

        {/* ===== CONTACT / DELETE ===== */}
        <motion.section {...reveal} transition={{ duration: 0.5 }}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-[#F8FAFC] dark:bg-gray-900 p-8">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: BLUE }}>
            <Mail size={20} strokeWidth={1.8} />
          </div>
          <h2 className="mt-5 font-serif text-2xl font-semibold text-gray-900 dark:text-white">Contact us or delete your data</h2>
          <p className="mt-3 leading-relaxed text-gray-600 dark:text-gray-300">
            The fastest way to remove yourself is the manage link in your{' '}
            <Link to="/account" className="font-semibold" style={{ color: BLUE }}>account settings</Link> — it deletes your profile
            from the pool right away. If you’d rather a human handle it, or you have any question about your data, reach out and we’ll
            take care of it.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-base font-semibold text-white transition-colors"
            style={{ background: BLUE }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4FD8')}
            onMouseLeave={(e) => (e.currentTarget.style.background = BLUE)}
          >
            Contact us <ArrowRight size={17} />
          </Link>
        </motion.section>
      </div>
    </div>
  );
}

export default Trust;
